import React, { useMemo } from 'react';
import { MenuItem } from '@mui/material';
import {
  ListPageFilterContainer,
  ListPageFilterCollapse,
  ListPageFilterTextField,
  ListPagePrimaryFilterRow,
  ListPageEqualSecondaryFilterRow,
  ListPageActiveFilters,
  type ListPageActiveFilterChip,
} from '../../common/list-page';
import type { ClientManagementFiltersState, LoyaltyTier } from './types';

const TIER_OPTIONS: { value: '' | LoyaltyTier; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'BRONZE', label: 'Bronce' },
  { value: 'SILVER', label: 'Plata' },
  { value: 'GOLD', label: 'Oro' },
  { value: 'PLATINUM', label: 'Platino' },
];

function renderLoyaltyTierSelectValue(value: unknown) {
  const v = (value ?? '') as '' | LoyaltyTier;
  return TIER_OPTIONS.find((o) => o.value === v)?.label ?? 'Todos';
}

export interface ClientManagementFiltersProps {
  filters: ClientManagementFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<ClientManagementFiltersState>>;
  appliedFilters: ClientManagementFiltersState;
  onClearAppliedField: (field: keyof ClientManagementFiltersState) => void;
  showMoreFilters: boolean;
  onSearch: () => void;
  onReset: () => void;
  onToggleMoreFilters: () => void;
}

const ClientManagementFilters: React.FC<ClientManagementFiltersProps> = ({
  filters,
  setFilters,
  appliedFilters,
  onClearAppliedField,
  showMoreFilters,
  onSearch,
  onReset,
  onToggleMoreFilters,
}) => {
  const handleKeyDownSearch = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSearch();
    }
  };

  const moreFiltersActiveCount =
    (filters.email.trim() !== '' ? 1 : 0) +
    (filters.phone.trim() !== '' ? 1 : 0) +
    (filters.loyaltyTier !== '' ? 1 : 0) +
    (filters.saleDateFrom.trim() !== '' ? 1 : 0) +
    (filters.saleDateTo.trim() !== '' ? 1 : 0);

  const activeFilterChips: ListPageActiveFilterChip[] = useMemo(() => {
    const chips: ListPageActiveFilterChip[] = [];
    const applied = appliedFilters;

    if (applied.searchTerm) {
      chips.push({
        key: 'searchTerm',
        label: `Búsqueda: ${applied.searchTerm}`,
        onRemove: () => onClearAppliedField('searchTerm'),
      });
    }
    if (applied.email) {
      chips.push({
        key: 'email',
        label: `Correo: ${applied.email}`,
        onRemove: () => onClearAppliedField('email'),
      });
    }
    if (applied.phone) {
      chips.push({
        key: 'phone',
        label: `Teléfono: ${applied.phone}`,
        onRemove: () => onClearAppliedField('phone'),
      });
    }
    if (applied.loyaltyTier) {
      const tierLabel =
        TIER_OPTIONS.find((o) => o.value === applied.loyaltyTier)?.label || applied.loyaltyTier;
      chips.push({
        key: 'loyaltyTier',
        label: `Lealtad: ${tierLabel}`,
        onRemove: () => onClearAppliedField('loyaltyTier'),
      });
    }
    if (applied.saleDateFrom) {
      chips.push({
        key: 'saleDateFrom',
        label: `Ventas desde: ${applied.saleDateFrom}`,
        onRemove: () => onClearAppliedField('saleDateFrom'),
      });
    }
    if (applied.saleDateTo) {
      chips.push({
        key: 'saleDateTo',
        label: `Ventas hasta: ${applied.saleDateTo}`,
        onRemove: () => onClearAppliedField('saleDateTo'),
      });
    }

    return chips;
  }, [appliedFilters, onClearAppliedField]);

  return (
    <ListPageFilterContainer>
      <ListPagePrimaryFilterRow
        showMoreFilters={showMoreFilters}
        onToggleMoreFilters={onToggleMoreFilters}
        onReset={onReset}
        onSearch={onSearch}
        moreFiltersActiveCount={moreFiltersActiveCount}
        searchFieldProps={{
          label: 'Buscar cliente',
          name: 'searchTerm',
          placeholder: 'Nombre, correo o teléfono',
          value: filters.searchTerm,
          onChange: (e) => setFilters((prev) => ({ ...prev, searchTerm: e.target.value })),
          onKeyDown: handleKeyDownSearch,
        }}
      />

      <ListPageFilterCollapse in={showMoreFilters} timeout={350} unmountOnExit>
        <ListPageEqualSecondaryFilterRow>
          <ListPageFilterTextField
            variant="outlined"
            label="Correo"
            name="email"
            value={filters.email}
            onChange={(e) => setFilters((prev) => ({ ...prev, email: e.target.value }))}
            onKeyDown={handleKeyDownSearch}
            size="medium"
            fullWidth
            placeholder="cliente@correo.com"
            InputLabelProps={{ shrink: true }}
          />
          <ListPageFilterTextField
            variant="outlined"
            label="Teléfono"
            name="phone"
            value={filters.phone}
            onChange={(e) => setFilters((prev) => ({ ...prev, phone: e.target.value }))}
            onKeyDown={handleKeyDownSearch}
            size="medium"
            fullWidth
            placeholder="+591 70000000"
            InputLabelProps={{ shrink: true }}
          />
          <ListPageFilterTextField
            variant="outlined"
            select
            label="Nivel de lealtad"
            name="loyaltyTier"
            value={filters.loyaltyTier}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                loyaltyTier: e.target.value as '' | LoyaltyTier,
              }))
            }
            size="medium"
            fullWidth
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true, renderValue: renderLoyaltyTierSelectValue }}
          >
            {TIER_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </ListPageFilterTextField>
          <ListPageFilterTextField
            variant="outlined"
            label="Ventas desde"
            name="saleDateFrom"
            type="date"
            value={filters.saleDateFrom}
            onChange={(e) => setFilters((prev) => ({ ...prev, saleDateFrom: e.target.value }))}
            size="medium"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <ListPageFilterTextField
            variant="outlined"
            label="Ventas hasta"
            name="saleDateTo"
            type="date"
            value={filters.saleDateTo}
            onChange={(e) => setFilters((prev) => ({ ...prev, saleDateTo: e.target.value }))}
            size="medium"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </ListPageEqualSecondaryFilterRow>
      </ListPageFilterCollapse>

      <ListPageActiveFilters chips={activeFilterChips} />
    </ListPageFilterContainer>
  );
};

export default ClientManagementFilters;
